import { useResolvedCompanyCtx } from '@contexts/ResolvedCompanyContext/ResolvedCompanyProvider'
import * as React from 'react'

import { TFProductCategory, useQWaiterProductList } from '~graphql/generated/clientGraphql'
import { TQWaiterProductListVariables } from '~graphql/generated/serverGraphql'
import { isNotEmptyCategory, useReplacePrice } from '~shared/segments/ProductList'

type TCategories = Nullable<RoA<TFProductCategory>>
/**
 * handles product list (categories) fetching
 */
export const useLoadProductList = () => {
  const { resolvedBranch, sector } = useResolvedCompanyCtx()

  // count of the all available categories, NOT the filtered ones
  const [productCategoryCount, setProductCategoryCount] = React.useState<Nullable<number>>(null)
  const [productCategories, setProductCategories] = React.useState<TCategories>(null)

  const qProductListVariables: TQWaiterProductListVariables = React.useMemo(() => {
    const branchId = resolvedBranch.id
    const sectorId = sector?.id ?? null
    const priceType = sector?.warePriceType.enum ?? null
    return {
      branchId,
      sectorId,
      priceType,
    }
  }, [resolvedBranch.id, sector])

  const { data } = useQWaiterProductList({
    variables: qProductListVariables,
  })

  // set total (available) categories count when they are loaded (once)
  React.useEffect(() => {
    if (!data) {
      setProductCategoryCount(null)
      return
    }

    const nonEmptyCategories = data.productCategories.filter(isNotEmptyCategory)
    setProductCategories(nonEmptyCategories)
    setProductCategoryCount(nonEmptyCategories.length)
  }, [data])

  const replacePrices = useReplacePrice({ variables: qProductListVariables })
  const filteredProductCategories = React.useMemo(() => {
    if (!productCategories) return null
    if (!replacePrices) return null

    return productCategories.map(replacePrices)
  }, [productCategories, replacePrices])

  return { productCategories: filteredProductCategories, productCategoryCount, replacePrices }
}
